import React, {
    FC, useEffect, useRef, useState,
} from 'react';

/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Named email region must accept keyboard scrolling. */

import { Divider } from 'antd';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { CodeAuthenticatorSnapshotProvider } from '@/components/Common/CodeAuthenticator';
import {
    useStores,
    useTargetScreen,
    useLoginMethodPreference,
    useAuthResolved,
    LOGIN_METHOD_EMAIL,
} from '@/components/hooks';
import {
    JUST_LOGGED_IN_TIMESTAMP_KEY,
    JUST_LOGGED_IN_TIMEOUT_MS,
    isPhoneMfaCompletedForLink,
} from '@/components/MiniApps/Common/constants';
import { getLinkIdFromLocation } from '@/components/hooks/useOTPFlow/helpers';

import { FileAccessPopupsProvider } from './FileAccessPopupsContext';
import FilesActionsProvider from './FilesActionsContext';
import FilesRow from './FilesRow';
import OpenSecurely from './FilesRow/ActionsColumn/OpenSecurely';
import FilesCarousel from './FilesCarousel';
import Greeting from './Greeting';
import { MessageView } from './MessageView';
import {
    useVerifyAction,
    useOTPState,
    useOTPToggle,
    useDownloadActionError,
} from './hooks';
import { getContentType } from './helpers';
import styles from './LinkView.module.scss';
import OTPAuthSteps from '@/components/MiniApps/Common/OTPAuthSteps';
import {
    REQUIRED_PASSWORD_VERIFICATION,
} from '@/consts';
import { BASEURL, ENDPOINTS } from '@/api';
import { OpenOptions } from '@/config/openOptions.ts';
import { checkMessageContainsPermissions } from '@/components/utils';
import SkeletonView from '@/components/MiniApps/RecipientPage/InnerContent/SkeletonView';

const LinkView: FC = observer(() => {
    const [stepsInitialized, setStepsInitialized] = useState(false);
    const [mfaStatusLoaded, setMfaStatusLoaded] = useState(false);
    const isMobile = useTargetScreen('md');
    const { getLoginMethodPreference } = useLoginMethodPreference();

    const stepsMounted = (): void => {
        setStepsInitialized(true);
    };

    const {
        authSettingsStore: { API },
        recipientPageStore: { sharedFilesStore, emailMessageStore },
        filesAccessStore,
        userStore,
    } = useStores();

    const {
        authRequirements,
        accessibleFilesAllowForwarding,
        attachmentsCount,
        setIsContentLoading,
        isMainDataLoaded,
        isMFARequired,
        setIsMFARequired,
        accessibleFiles,
        isContentLoading,
        updateRequestsMap,
        anyRequestInProcess,
        permissionsLoaded,
        allPermissionsFail,
        setErrorType,
    } = sharedFilesStore;
    const { selectedAccessFileId } = filesAccessStore;
    const { messageId } = emailMessageStore;

    //after auth, hydrate sender/recipient emails from /ext/links
    useEffect(() => {
        if (userStore.isUserSetUp && sharedFilesStore.linkData && !sharedFilesStore.linkData.src_email) {
            void sharedFilesStore.tryEnrichAuthenticatedLinkDetails();
        }
    }, [userStore.isUserSetUp, sharedFilesStore.linkData?.link_id]);

    const {
        showOTP,
        closeOTP,
        openOTP,
    } = useOTPState();

    const {
        verifyAccess,
        postAuthAction,
        setAction,
        lastAccessType,
    } = useVerifyAction(sharedFilesStore, API);

    useOTPToggle({
        showOTP,
        closeOTP,
        openOTP,
        filesAccessStore,
        sharedFilesStore,
        userStore,
        lastAccessType,
        postAuthAction,
        messageId,
    });

    useDownloadActionError(filesAccessStore, sharedFilesStore, openOTP);

    const { phone: hasPhoneVerification, password: hasPasswordVerification } = authRequirements;
    const requireStandardLogin = !!authRequirements.standardLogin;
    const hasAllowForwarding = accessibleFilesAllowForwarding;

    const isMFAReqiredNeeded = (hasPhoneVerification || hasPasswordVerification) ? isMFARequired : false;

    const isAuthorized = userStore.isUserSetUp
        && mfaStatusLoaded
        && !isMFAReqiredNeeded
        && permissionsLoaded
        && !allPermissionsFail;

    // Gate the file view on `anyRequestInProcess` only until it has been shown
    // once. After that, transient background requests (MFA re-probe, permission
    // refreshes) must not de-authorize the view: unmounting FilesCarousel tears
    // down the viewer iframe, which recipients see as the page "reloading" (and
    // as continuous PDF flicker on slower Android devices). Real authorization
    // changes (MFA required, permissions revoked) still switch the content type
    // via `isAuthorized` itself.
    const hasShownFileViewRef = useRef(false);
    // OTP opened while logged out must stay mountable after email Cognito signs
    // the recipient in. Until then mfaProbeResolved is true via !isUserSetUp; the
    // moment isUserSetUp flips true, mfaStatusLoaded is often still false (files /
    // permissions are deferred), so gating contentType on the probe alone would
    // unmount OTPAuthSteps mid email→phone and leave an empty share view.
    // Do not use showOTP alone here: on refresh for an already-signed-in user
    // showOTP starts true and would flash Phone Verification before the probe.
    const startedOtpWhileLoggedOutRef = useRef(false);

    // Cognito session restore is asynchronous on reload. Don't surface the OTP
    // screen until the session has resolved (`authState` left `loading`) and the
    // MFA probe finished, otherwise an already-verified recipient briefly sees the
    // Phone Verification screen (and OTPAuthSteps mounts and re-sends a code)
    // before the state settles. The default `isContentLoading` keeps SkeletonView
    // up while we wait. `useAuthResolved` includes a timeout fallback so a session
    // that never settles can't leave the recipient permanently stuck on the loader.
    const authResolved = useAuthResolved(userStore.authState);

    useEffect(() => {
        if (showOTP && !userStore.isUserSetUp) {
            startedOtpWhileLoggedOutRef.current = true;
        }
    }, [showOTP, userStore.isUserSetUp]);

    // File metadata (and therefore the permission/MFA probe) is deferred until
    // the recipient authenticates (SPX-6450). For a not-yet-authenticated
    // recipient `permissionsLoaded`/`mfaStatusLoaded` never flip true, so gating
    // the OTP screen on the MFA probe would deadlock them on SkeletonView. The
    // probe only matters for an already-authenticated recipient, where it avoids
    // flashing the OTP screen at an already-verified user.
    const mfaProbeResolved = !userStore.isUserSetUp
        || mfaStatusLoaded
        || startedOtpWhileLoggedOutRef.current;

    const contentType = getContentType({
        showOTP: showOTP && authResolved && mfaProbeResolved,
        selectedFileId: selectedAccessFileId === messageId ? null : selectedAccessFileId,
        isAuthorized: (hasShownFileViewRef.current || !anyRequestInProcess) && isAuthorized,
    });

    if (contentType === 'fileView') {
        hasShownFileViewRef.current = true;
    }

    const showOpenViewerButton = contentType !== 'fileView';

    // `accessibleFiles` is a mobx @computed that returns a NEW array identity on
    // every `filesPermissions` write (e.g. background tryRefreshPermissions while a
    // large file is still being secured). Using the array itself as an effect
    // dependency re-triggered the MFA probe in a loop, flipping `anyRequestInProcess`
    // / `mfaStatusLoaded` and remounting the carousel (greeting <-> "Still securing
    // file" oscillation). Key the effect by the accessible file ids instead, and read
    // the latest list through a ref inside the async probe.
    const accessibleFilesRef = useRef(accessibleFiles);
    accessibleFilesRef.current = accessibleFiles;
    const accessibleFilesKey = accessibleFiles.map(({ file_id: fileId }) => fileId).join(',');

    useEffect(() => {
        async function checkIsMFARequired(): Promise<void> {
            updateRequestsMap('checkIsMFARequired', true);
            try {
                const mfaFile = accessibleFilesRef.current.find((item) => item.policy.isMfa);
                // Also detect password-only policies: policy.isMfa is false for them,
                // but an openFile call will still return 401 if the password hasn't been entered.
                const passwordFile = accessibleFilesRef.current.find((item) => item?.policy?.settings?.access?.some(
                    (setting) => setting.id === REQUIRED_PASSWORD_VERIFICATION && setting.value === true,
                ));
                const fileToCheck = mfaFile || passwordFile;
                if (!fileToCheck) {
                    setIsMFARequired(false);
                    return;
                }

                const filesWithMfa = fileToCheck.file_id;
                const result = await API.post(
                    BASEURL.backend(),
                    ENDPOINTS.openFile(filesWithMfa),
                    { body: { app_name: OpenOptions.viewer } },
                );
                if (result) {
                    // Cognito email auth alone can make /open succeed before phone
                    // OTP. Keep MFA required until phone is completed this session.
                    if (hasPhoneVerification && !isPhoneMfaCompletedForLink(getLinkIdFromLocation())) {
                        setIsMFARequired(true);
                    } else {
                        setIsMFARequired(false);
                    }
                }
            } catch (error) {
                const { response } = (error || {}) as { response?: { data?: { message?: string }; status?: number } };
                const errorMessage = response?.data?.message;
                const errorStatus = response?.status;
                const mfaMessage = 'Mfa required';
                const permissionsMessage = 'User doesn\'t have permissions for this operation.';
                const passwordMessage = 'Missing protection password';

                // 406 means the viewer app is not allowed by the file's policy; it is not
                // an MFA signal. Treating it as MFA required would incorrectly block the page.
                if (errorStatus === 406) {
                    setIsMFARequired(false);
                    return;
                }

                // 5xx / infra failures (e.g. RoleArn misconfig) are not MFA signals.
                // Treating them as MFA required re-opens OTP on every refresh.
                if (typeof errorStatus === 'number' && errorStatus >= 500) {
                    console.error('MFA probe openFile failed with server error', error);
                    return;
                }

                const isExplicitMfaRequired = (
                    errorMessage === mfaMessage
                    || errorMessage === passwordMessage
                    || (hasAllowForwarding && errorMessage === permissionsMessage)
                );
                // For logged-in users, any openFile failure that is NOT a permissions error
                // means MFA is still required. This covers:
                //   • 403 adminAccessDenied (backend message that doesn't match mfaMessage)
                //   • CORS-blocked 403 responses (error.response is null → errorMessage is
                //     undefined), which happen on localhost when the backend omits CORS
                //     headers from error responses.
                const isUserLoggedIn = userStore.isUserSetUp;
                const isPermissionsError = !hasAllowForwarding && checkMessageContainsPermissions(errorMessage);
                if (isExplicitMfaRequired || (isUserLoggedIn && !isPermissionsError)) {
                    setIsMFARequired(true);
                }
            } finally {
                updateRequestsMap('checkIsMFARequired', false);
                setMfaStatusLoaded(true);
            }
        }

        if (permissionsLoaded) {
            if (hasPhoneVerification || hasPasswordVerification) {
                // While auth-flow OTP is open, skip the openFile MFA probe.
                // After email Cognito auth an early open can succeed before phone
                // (or password) finishes; probing then would clear isMFARequired and
                // unmount OTP. Do not force isMFARequired=true here — OTPAuthSteps
                // owns that flag; re-asserting it after MFA completes races with
                // closeOTP and can reopen OTP so the attachment never appears.
                if (showOTP) {
                    setMfaStatusLoaded(true);
                    return;
                }
                // Keep the previous MFA status while a re-check is in flight instead
                // of resetting it: dropping `mfaStatusLoaded` to false de-authorizes
                // the view mid-session and unmounts the carousel (visible as a
                // "reload" for the recipient). The initial state is already false,
                // so the first load still waits for the probe result.
                checkIsMFARequired();
            } else {
                setIsMFARequired(false);
                setMfaStatusLoaded(true);
            }
        }
    }, [
        API,
        accessibleFilesKey,
        hasPasswordVerification,
        hasPhoneVerification,
        permissionsLoaded,
        setIsMFARequired,
        showOTP,
        updateRequestsMap,
    ]);

    useEffect(() => {
        // For a logged-in recipient `mfaProbeResolved === mfaStatusLoaded`, which
        // can only be true after `permissionsLoaded`, so this preserves the
        // previous gate. For an unauthenticated recipient the probe is skipped and
        // we proceed to open the OTP screen once auth has resolved.
        if (!anyRequestInProcess && isMainDataLoaded && authResolved && mfaProbeResolved) {
            const loginPreference = getLoginMethodPreference();
            const willRedirectToLogin = !userStore.isUserSetUp && (
                loginPreference === LOGIN_METHOD_EMAIL
                || requireStandardLogin
            );

            const justLoggedInTimestamp = sessionStorage.getItem(JUST_LOGGED_IN_TIMESTAMP_KEY);
            const hasJustLoggedIn = justLoggedInTimestamp
                && (Date.now() - parseInt(justLoggedInTimestamp, 10)) < JUST_LOGGED_IN_TIMEOUT_MS;
            const suppressedByJustLoggedIn = hasJustLoggedIn && !isMFAReqiredNeeded;
            if ((!userStore.isUserSetUp || isMFAReqiredNeeded) && !suppressedByJustLoggedIn && !willRedirectToLogin) {
                openOTP();
            } else {
                closeOTP();
                setIsContentLoading(false);
            }
        }
    }, [
        anyRequestInProcess,
        authResolved,
        isMFAReqiredNeeded,
        isMainDataLoaded,
        mfaProbeResolved,
        mfaStatusLoaded,
        openOTP,
        permissionsLoaded,
        requireStandardLogin,
        setIsContentLoading,
        userStore.isUserSetUp,
        getLoginMethodPreference,
    ]);

    const allPermissionsError = allPermissionsFail && permissionsLoaded;

    useEffect(() => {
        if (allPermissionsError && !hasAllowForwarding) {
            setErrorType('noPermissions');
        }
    }, [allPermissionsError, hasAllowForwarding, setErrorType]);

    return (
        <FilesActionsProvider
            openOTP={openOTP}
            setAction={setAction}
        >
            <FileAccessPopupsProvider>
                <CodeAuthenticatorSnapshotProvider>
                    <div
                        className={classNames(styles.container, {
                            [styles['new-ui']]: contentType === 'OTP',
                            [styles['email-content']]: contentType === 'message',
                        })}
                    >
                        {contentType === 'OTP' && (
                            <OTPAuthSteps
                                openFile={verifyAccess}
                                closeOTP={closeOTP}
                                stepsInitialized={stepsInitialized}
                                stepsMounted={stepsMounted}
                            />
                        )}
                        {/* Initial-load gating on anyRequestInProcess is folded into
                            contentType above; re-checking it here would unmount the
                            viewer on every background request. */}
                        {contentType === 'fileView' && <FilesCarousel />}
                        {contentType === 'message' && !isContentLoading && (
                            <div
                                className={classNames(styles['main-content-wrapper'], styles['email-scroll'])}
                                role="region"
                                aria-label="Recipient message"
                                tabIndex={0}
                            >
                                <div className={styles['main-content-column']}>
                                    <Greeting />
                                    <Divider
                                        className={classNames({
                                            [styles.divider]: !isMobile,
                                            [styles['mobile-divider']]: isMobile,
                                        })}
                                    />
                                    {contentType === 'message' && <MessageView />}
                                </div>
                                {isMobile && !!attachmentsCount && <OpenSecurely />}
                            </div>
                        )}
                        {contentType !== 'OTP' && !isContentLoading && (
                            <FilesRow
                                showOpenViewerButton={showOpenViewerButton}
                                contentType={contentType}
                            />
                        )}
                        {contentType !== 'OTP' && isContentLoading && (
                            <SkeletonView />
                        )}
                    </div>
                </CodeAuthenticatorSnapshotProvider>
            </FileAccessPopupsProvider>
        </FilesActionsProvider>

    );
});

export default LinkView;
/* eslint-enable jsx-a11y/no-noninteractive-tabindex */
